import { Badge, Box, Group, Stack, Title } from "@mantine/core"
import { IconEdit } from "@tabler/icons-react"
import {
  ActionIcon,
  Card,
  DEFAULT_STYLING_PARAMETERS,
  DesignStudioParametersToolkit,
  RenderFormattedText,
  renderTextParagraphContent,
  renderBulletedList,
} from "@tfg/ui"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import {
  ContentBlockSchema,
  ContentBlock,
  TextContentJSONSchema,
  FormattedTextContentSchema,
} from "@tfg/types/schemas/communication-studio/components"
import { ProductSheetLanguage } from "@tfg/types/schemas/languages"
import { ComponentTypesEnum } from "@tfg/types/types/communication-studio/components.types"

import { GetProductPageComponentsResponseDto } from "~/gen/api"

type Props = {
  component: GetProductPageComponentsResponseDto
  handleEdit: (component: GetProductPageComponentsResponseDto) => void
  language: ProductSheetLanguage
}

const getContent = (
  component: GetProductPageComponentsResponseDto,
  language: ProductSheetLanguage
): ContentBlock[] | null => {
  const contentData = z
    .array(ContentBlockSchema)
    .safeParse(component.content[language])
  if (!contentData.success) return null
  const content: ContentBlock[] = contentData.data
  if (!content) return null
  return content
}

export const TranslationCard = ({ component, handleEdit, language }: Props) => {
  const { t } = useTranslation("communicationStudio")
  useEffect(() => {
    DesignStudioParametersToolkit.injectCssVariablesToDocumentHead(
      DEFAULT_STYLING_PARAMETERS
    )
  }, [])
  const content = getContent(component, language)
  if (!content) return null

  return (
    <Group justify="space-between" w="100%" wrap="nowrap">
      <Card h="100%" w="100%" pos="relative">
        <Stack>
          <Title order={5}>
            <Badge variant="info">
              {t(`tabs.content.components.${component.componentType}`)}
            </Badge>
          </Title>
          {content.map((block, index) => {
            if (
              [
                ComponentTypesEnum.TEXT_PARAGRAPH,
                ComponentTypesEnum.GENERIC_PASSPORT_TITLE,
              ].includes(block.type)
            ) {
              const textContent = z
                .array(TextContentJSONSchema)
                .safeParse(block.textContent)
              if (!textContent.success) return null
              return renderTextParagraphContent(
                textContent.data,
                index,
                DEFAULT_STYLING_PARAMETERS
              )
            } else if (block.type === ComponentTypesEnum.TEXT_PARAGRAPH_V2) {
              const formattedTextContent =
                FormattedTextContentSchema.array().safeParse(block.textContent)
              if (!formattedTextContent.success) return null

              return (
                <RenderFormattedText
                  formattedTextContent={formattedTextContent.data}
                  key={`${index}-${language}`}
                />
              )
            } else if (block.type === ComponentTypesEnum.BULLETED_LIST) {
              return renderBulletedList(block)
            }
            return null
          })}
        </Stack>
        <Box pos="absolute" top={0} right={0} p="xs">
          <ActionIcon
            icon={<IconEdit />}
            onClick={() => handleEdit(component)}
          />
        </Box>
      </Card>
    </Group>
  )
}
